import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Search, Settings2, SlidersHorizontal } from "lucide-react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import useDebounce from "../../../hooks/useDebounce";

const ENQUIRY_API = "https://s-ten-ochre.vercel.app/api/enquiry";
const API_BASE = import.meta.env.VITE_API_URL || "https://km-lkmy.onrender.com";
const PLACEHOLDER_IMAGE = "/placeholder.jpg";

const SearchSection = () => {
  const [keyword, setKeyword] = useState("");
  const [brand, setBrand] = useState("");
  const [category, setCategory] = useState("");

  const [loading, setLoading] = useState(false);

  // PAGINATED DATA
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const itemsPerPage = 8;

  // Scroll reference
  const sectionRef = useRef(null);

  const cacheRef = useRef(new Map());

  // MODALS
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [detailsProduct, setDetailsProduct] = useState(null);

  // FORM DATA
  const [formData, setFormData] = useState({
    name: "",
    contact: "",
    email: "",
    address: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);

  // DEBOUNCED FILTERS
  const debouncedKeyword = useDebounce(keyword, 300);
  const debouncedBrand = useDebounce(brand, 300);
  const debouncedCategory = useDebounce(category, 300);

  // -----------------------------
  // Fetch Products
  // -----------------------------
  const fetchProducts = async (pageNumber = 1) => {
    const cacheKey = `${pageNumber}-${debouncedKeyword}-${debouncedBrand}-${debouncedCategory}`;

    // ✅ USE CACHE IF AVAILABLE
    if (cacheRef.current.has(cacheKey)) {
      const cached = cacheRef.current.get(cacheKey);
      setProducts(cached.products);
      setTotalPages(cached.totalPages);
      setPage(cached.page);
      return;
    }

    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.set("page", pageNumber);
      params.set("limit", itemsPerPage);

      if (debouncedKeyword) params.set("search", debouncedKeyword);
      if (debouncedBrand) params.set("brand", debouncedBrand);
      if (debouncedCategory) params.set("category", debouncedCategory);

      const res = await fetch(`${API_BASE}/api/products?${params.toString()}`);
      const data = await res.json();

      const payload = {
        products: data.products || [],
        totalPages: data.totalPages || 1,
        page: data.page || 1,
      };

      setProducts(payload.products);
      setTotalPages(payload.totalPages);
      setPage(payload.page);

      // ✅ STORE IN CACHE
      cacheRef.current.set(cacheKey, payload);

      // Scroll AFTER results load
      if (sectionRef.current) {
        const offset = sectionRef.current.offsetTop - 40;
        window.scrollTo({ top: offset, behavior: "smooth" });
      }
    } catch (err) {
      console.error("❌ Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // -----------------------------
  // When filters change → reset page
  // -----------------------------
  useEffect(() => {
    fetchProducts(1);
  }, [debouncedKeyword, debouncedBrand, debouncedCategory]);

  // -----------------------------
  // When page changes
  // -----------------------------
  useEffect(() => {
    fetchProducts(page);
  }, [page]);

  // -----------------------------
  // Handlers
  // -----------------------------
  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducts(1);
  };

  const handleFormInput = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedProduct) return;

    if (
      !formData.name.trim() ||
      !formData.contact.trim() ||
      !formData.email.trim() ||
      !formData.address.trim()
    ) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      setSubmitting(true);

      const enquiryData = {
        ...formData,
        partName: selectedProduct.part_name,
        partNumber: selectedProduct.part_number,
        brand: selectedProduct.brand,
      };

      const res = await fetch(ENQUIRY_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(enquiryData),
      });

      if (res.ok) {
        alert("Your enquiry has been submitted!");
        setSelectedProduct(null);
        setFormData({
          name: "",
          contact: "",
          email: "",
          address: "",
          message: "",
        });
      } else {
        alert("Something went wrong. Try again later.");
      }
    } catch (error) {
      console.error("❌ Enquiry error:", error);
      alert("Error sending enquiry.");
    } finally {
      setSubmitting(false);
    }
  };

  const productKey = (item) =>
    item.id || item._id || item.part_number || Math.random();

  const SkeletonCard = () => (
    <div className="border rounded-xl bg-white p-3 animate-pulse">
      <div className="h-28 bg-gray-200 rounded mb-3" />
      <div className="h-4 bg-gray-200 rounded mb-2" />
      <div className="h-3 bg-gray-200 rounded mb-2" />
      <div className="h-3 bg-gray-200 rounded w-2/3" />
    </div>
  );

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-gradient-to-br from-gray-50 via-white to-gray-100 py-20 px-4 flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,0,40,0.05),transparent_70%)] pointer-events-none"></div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 max-w-6xl w-full text-center"
      >
        {/* Heading */}
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
          Buy Genuine Auto-Parts and Accessories Online
        </h2>
        <p className="text-gray-600 mb-10">
          Search any original car spare parts and accessories for Maruti Suzuki,
          Hyundai, Tata Motors, Mahindra, Toyota and more.
        </p>

        {/* Search Bar */}
        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row flex-wrap items-stretch gap-3 bg-white/80 rounded-2xl shadow-lg border p-3 max-w-4xl mx-auto"
        >
          {/* Brand */}
          <div className="relative sm:w-48 w-full">
            <Settings2
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <select
              value={brand}
              onChange={(e) => setBrand(e.target.value)}
              className="w-full border rounded-xl py-2 pl-9 pr-4 text-gray-800"
            >
              <option value="">All Brands</option>
              <option>Maruti Suzuki</option>
              <option>Hyundai</option>
              <option>Mahindra</option>
              <option>Tata</option>
              <option>Honda</option>
              <option>Renault</option>
              <option>Toyota</option>
              <option>MG Motors</option>
              <option>Kia</option>
              <option>Volkswagen</option>
              <option>Skoda</option>
              <option>Others</option>
            </select>
          </div>

          {/* Category */}
          <div className="relative sm:w-48 w-full">
            <SlidersHorizontal
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border rounded-xl py-2 pl-9 pr-4 text-gray-800"
            >
              <option value="">All Categories</option>
              <option>Transmission</option>
              <option>Trim</option>
              <option>Wire</option>
              <option>Engine</option>
              <option>Body</option>
              <option>Chassis</option>
              <option>Electric</option>
            </select>
          </div>

          {/* Keyword */}
          <div className="relative flex-1">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="Search by part name or number..."
              className="w-full border rounded-xl py-2 pl-9 pr-4"
            />
          </div>

          <Button
            type="submit"
            className="bg-blue-700 text-white px-6 py-2 rounded-xl"
          >
            Search
          </Button>
        </form>

        {/* Results */}

        <div className="mt-12">
          {loading && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </div>
          )}

          {!loading && products.length > 0 && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 bg-gray-100 p-6 rounded-2xl border shadow-inner">
                {products.map((item) => (
                  <div
                    key={productKey(item)}
                    className="border rounded-xl bg-white p-3 flex flex-col hover:shadow-md transition"
                  >
                    <img
                      src={item.image || PLACEHOLDER_IMAGE}
                      alt={item.part_name}
                      className="w-full h-28 object-contain mb-3"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = PLACEHOLDER_IMAGE;
                      }}
                    />

                    <div className="font-medium text-gray-900 truncate">
                      {item.part_name}
                    </div>

                    <div className="text-gray-500 truncate">
                      {item.part_number}
                    </div>

                    <div className="text-gray-500">{item.brand}</div>
                    <div className="text-gray-500 mb-2">{item.category}</div>

                    <Button
                      size="sm"
                      className="w-full bg-blue-600 text-white rounded-md"
                      onClick={() => setSelectedProduct(item)}
                    >
                      Enquire Now
                    </Button>

                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full mt-2 border-blue-600 text-blue-600 rounded-md hover:bg-blue-600 hover:text-white transition"
                      onClick={() => setDetailsProduct(item)}
                    >
                      Details
                    </Button>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  className={`px-4 py-2 rounded-lg border ${
                    page === 1
                      ? "bg-gray-200 text-gray-400"
                      : "bg-white hover:bg-gray-100"
                  }`}
                >
                  ⟵ Prev
                </button>

                <span className="text-sm text-gray-700">
                  Page {page} of {totalPages}
                </span>

                <button
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  className={`px-4 py-2 rounded-lg border ${
                    page === totalPages
                      ? "bg-gray-200 text-gray-400"
                      : "bg-white hover:bg-gray-100"
                  }`}
                >
                  Next ⟶
                </button>
              </div>
            </>
          )}

          {!loading && products.length === 0 && (
            <div className="p-6 text-center text-gray-600">
              <Icon
                name="AlertCircle"
                size={28}
                className="mx-auto mb-2 text-blue-500"
              />
              <p className="font-medium">No products found for your search.</p>
              <p className="text-sm">
                Reach out to us - we'll help you find the part.
              </p>

              <a href="/contact">
                <Button className="mt-3 bg-blue-600 text-white">
                  Contact Us
                </Button>
              </a>
            </div>
          )}
        </div>
      </motion.div>

      {/* Enquiry Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setSelectedProduct(null)}
              className="absolute top-3 right-3 text-gray-500"
            >
              ✕
            </button>

            <h2 className="text-xl font-bold mb-2">Enquire Now</h2>

            <p className="text-sm text-gray-600 mb-4">
              <b>{selectedProduct.part_name}</b> ({selectedProduct.part_number})
              <br />
              <span className="text-xs text-gray-500">
                Brand: {selectedProduct.brand}
              </span>
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <input
                required
                type="text"
                name="name"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleFormInput}
              />

              <input
                required
                type="text"
                name="contact"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Contact Number"
                value={formData.contact}
                onChange={handleFormInput}
              />

              <input
                required
                type="email"
                name="email"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Email"
                value={formData.email}
                onChange={handleFormInput}
              />

              <textarea
                required
                name="address"
                rows="3"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Address"
                value={formData.address}
                onChange={handleFormInput}
              />

              <textarea
                name="message"
                rows="3"
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Message (optional)"
                value={formData.message}
                onChange={handleFormInput}
              />

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-blue-600 text-white"
              >
                {submitting ? "Sending..." : "Submit Enquiry"}
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {detailsProduct && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl p-6 relative">
            <button
              onClick={() => setDetailsProduct(null)}
              className="absolute top-3 right-3 text-gray-500"
            >
              ✕
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center justify-center">
                <img
                  src={detailsProduct.image || PLACEHOLDER_IMAGE}
                  alt={detailsProduct.part_name}
                  className="w-full h-64 object-contain"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = PLACEHOLDER_IMAGE;
                  }}
                />
              </div>

              <div className="space-y-4">
                <h2 className="text-2xl font-bold">
                  {detailsProduct.part_name}
                </h2>

                <p className="text-gray-600">
                  Part Number:{" "}
                  <span className="font-medium">
                    {detailsProduct.part_number}
                  </span>
                </p>

                <p className="text-gray-600">
                  Category:{" "}
                  <span className="font-medium">{detailsProduct.category}</span>
                </p>

                <p className="text-gray-700">
                  {detailsProduct.description || "No description available."}
                </p>

                <Button
                  size="lg"
                  className="w-full bg-blue-600 text-white"
                  onClick={() => {
                    setSelectedProduct(detailsProduct);
                    setDetailsProduct(null);
                  }}
                >
                  Enquire Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default SearchSection;
